import React from 'react';

// Icons defined outside component to avoid re-creation on render
const UsersIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const MenuDotsIcon = () => (
  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
  </svg>
);

/**
 * HiveCard - A reusable component for displaying hive information
 * 
 * Variants:
 * - "list": Compact row layout (for My Hives, Search Results)
 * - "discover": Row with join action (for Discover tabs)
 * 
 * @param {Object} hive - Hive data object
 * @param {string} variant - Display variant: "list" | "discover" (default: "list")
 * @param {Function} onOpen - Callback when hive name/icon is clicked
 * @param {Function} onJoin - Callback when Join button is clicked (discover variant)
 * @param {Function} onMenuClick - Callback when menu button is clicked (list variant)
 * @param {boolean} showMenu - Whether menu button is visible (list variant)
 * @param {boolean} isMenuOpen - Whether dropdown menu is open
 * @param {React.ReactNode} menuContent - Content to render in dropdown menu
 * @param {boolean} showTags - Whether to display tags
 * @param {boolean} showUnread - Whether to show unread indicator
 * @param {string} className - Additional CSS classes
 */
const HiveCard = ({
  hive,
  variant = 'list',
  onOpen,
  onJoin,
  onMenuClick,
  showMenu = false,
  isMenuOpen = false,
  menuContent,
  showTags = false,
  showUnread = true,
  className = ''
}) => {
  const baseClasses = variant === 'discover'
    ? 'flex items-center justify-between p-3 bg-white rounded-lg border border-gray-100'
    : 'bg-white rounded-xl border border-gray-100';

  return (
    <div 
      className={`${baseClasses} ${className}`}
      data-testid={`hive-card-${hive.hive_id}`}
    >
      <div className={variant === 'list' ? 'p-3 flex items-center gap-3' : 'flex items-center gap-3 flex-1 min-w-0'}>
        {/* Icon with optional unread indicator */}
        <div className="relative flex-shrink-0">
          <span className="text-2xl">{hive.icon}</span>
          {showUnread && hive.has_unread && (
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full border-2 border-white"></span>
          )}
        </div>
        
        {/* Hive Info */}
        <div className="flex-1 min-w-0">
          <button
            onClick={() => onOpen?.(hive.hive_id)}
            className="font-medium text-amber-700 hover:text-amber-800 hover:underline truncate block text-left"
            data-testid={`hive-card-name-${hive.hive_id}`}
          >
            {hive.name}
            {hive.hive_type === 'private' && (
              <span className="ml-1 text-xs">🔒</span>
            )}
          </button>
          <div className="text-xs text-gray-500 flex items-center gap-2 flex-wrap">
            <span className="flex items-center gap-1">
              <UsersIcon /> {hive.member_count || 0} members
            </span>
            {hive.posting_mode === 'queens' && (
              <span className="text-amber-600">👑 Curated</span>
            )}
            {hive.my_role === 'queen_bee' && variant === 'list' && (
              <span className="text-amber-600">👑 Creator</span>
            )}
          </div>
          
          {/* Description (optional) */}
          {hive.description && showTags && (
            <p className="text-xs text-gray-400 mt-1 line-clamp-1">{hive.description}</p>
          )}
          
          {/* Tags */}
          {showTags && hive.tags && hive.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {hive.tags.slice(0, 3).map((tag, i) => (
                <span key={i} className="text-xs text-amber-600">#{tag}</span>
              ))}
            </div>
          )}
        </div>
        
        {/* List variant: Menu button */}
        {variant === 'list' && showMenu && (
          <div className="relative flex-shrink-0">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMenuClick?.(hive.hive_id);
              }}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              data-testid={`hive-menu-btn-${hive.hive_id}`}
            >
              <MenuDotsIcon />
            </button>
            
            {/* Dropdown Menu */}
            {isMenuOpen && menuContent && (
              <div className="absolute right-0 top-10 z-50 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[140px]">
                {menuContent}
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Discover variant: Join action */}
      {variant === 'discover' && (
        <>
          {hive.is_member ? (
            <span className="text-sm text-green-600 font-medium flex-shrink-0">Joined</span>
          ) : (
            <button
              onClick={() => onJoin?.(hive.hive_id)}
              className="px-3 py-1 bg-amber-600 text-white text-sm rounded-full hover:bg-amber-700 flex-shrink-0"
              data-testid={`hive-join-btn-${hive.hive_id}`}
            >
              Join
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default HiveCard;
